import React, { PropsWithChildren, useEffect, useMemo, useState } from "react";

import { Stack } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import useAdvisorClientsV2 from "@fartherfinance/frontend/api/Entity/hooks/useAdvisorClientsV2";
import useGetTaskTypes from "@fartherfinance/frontend/api/TaskWorkflows/hooks/useGetTaskTypes";
import useGetTaskTypeTemplate from "@fartherfinance/frontend/api/TaskWorkflows/hooks/useGetTaskTypeTemplate";
import { FieldParameter } from "@fartherfinance/frontend/api/TaskWorkflows/Types";

import { useGetFieldOptions } from "../FormEngine/data/hooks/useGetFieldOptions";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import FormDropdownField from "@src/sharedComponents/Forms/FormDropdownField";
import Spacer from "@src/sharedComponents/Forms/Spacer";
import ModalWithClearButton from "@src/sharedComponents/Modal/ModalWithClearButton";
import Text from "@src/sharedComponents/Typography/Text";
import { State } from "@src/store";

import styles from "./CreateTaskModal.module.css";

interface TaskTypeOption {
  label: string;
  value: string;
  typeId: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: { taskType: string; client: string }) => void;
}

type FormValues = {
  taskType: TaskTypeOption | null;
  client: string;
};

const DEFAULT_CLIENT_PARAMETER: FieldParameter = {
  key: "client",
  dataType: {
    type: "EnumData",
    resourceType: {
      type: "Person",
      kinds: ["Client"],
    },
  },
  display: {
    type: "SelectField",
    primaryLabel: "Client",
  },
};

const CreateTaskModal: React.FC<PropsWithChildren<Props>> = ({
  onClose,
  isOpen,
  onSubmit,
}) => {
  const history = useHistory();
  const location = useLocation();

  const { advisorId } = useSelector((state: State) => ({
    advisorId: state.main_Reducer.cockroach_advisor_id,
  }));
  const auth = useAdvisorRequestAuth();
  const advisorClients = useAdvisorClientsV2(advisorId, auth);

  const methods = useForm<FormValues>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      taskType: null,
      client: "",
    },
  });

  const {
    formState: { isValid },
    watch,
    getValues,
  } = methods;

  const fieldOptionsForm = useForm<Record<string, unknown>>({
    defaultValues: {},
  });

  useEffect(() => {
    const values = getValues();
    Object.entries(values).forEach(([key, value]) => {
      fieldOptionsForm.setValue(key, value);
    });
  }, [getValues, fieldOptionsForm]);

  const handleClearField = (field: keyof FormValues) => {
    methods.setValue(field, field === "taskType" ? null : "");
  };

  const { data: taskTypesData, isLoading: isLoadingTaskTypes } =
    useGetTaskTypes(auth);

  const taskTypeOptions: TaskTypeOption[] = useMemo(() => {
    if (!taskTypesData?.data) {
      return [];
    }
    return taskTypesData.data.map((type) => ({
      label: type.displayName || type.name,
      value: type.name,
      typeId: type.typeId,
    }));
  }, [taskTypesData]);

  const selectedTaskType = watch("taskType");

  const { data: taskTypeTemplate } = useGetTaskTypeTemplate(
    selectedTaskType?.typeId ? selectedTaskType.typeId.toString() : null,
    auth
  );

  const [clientParameter, setClientParameter] = useState<FieldParameter>(
    DEFAULT_CLIENT_PARAMETER
  );

  useEffect(() => {
    if (!taskTypeTemplate) {
      setClientParameter(DEFAULT_CLIENT_PARAMETER);
      return;
    }

    const clientFieldDef = taskTypeTemplate.parameters.find(
      (param) => param.key === "client"
    );
    setClientParameter(clientFieldDef ?? DEFAULT_CLIENT_PARAMETER);
  }, [taskTypeTemplate]);

  const { options: clientOptions = [] } = useGetFieldOptions(
    clientParameter,
    fieldOptionsForm.getValues
  );

  const clientList = useMemo(() => {
    if (!advisorClients.data) {
      return [];
    }
    return advisorClients.data.clients.map((client) => ({
      label: `${client.name.first} ${client.name.last}`,
      value: client.clientId,
    }));
  }, [advisorClients]);

  const filteredClientList = useMemo(() => {
    if (clientOptions.length === 0) {
      return clientList;
    }
    const allowedIds = new Set(clientOptions.map((opt) => opt.value));
    return clientList.filter((entry) => allowedIds.has(entry.value));
  }, [clientOptions, clientList]);

  const onContinue = () => {
    const values = methods.getValues();
    onSubmit({
      taskType: values.taskType?.value || "",
      client: values.client,
    });
    history.push({
      pathname: "/Advisor/Tasks/CreateTask",
      search: location.search,
      state: {
        type: "createTask",
        data: {
          taskType: values.taskType?.value,
          taskTypeId: values.taskType?.typeId,
          client: values.client,
          taskTypeTemplate: taskTypeTemplate?.templateId,
        },
      },
    });
  };

  return (
    <ModalWithClearButton closeModal={onClose} isOpen={isOpen}>
      <Text variant="title2" className={styles.header}>
        Create task
      </Text>

      <FormProvider {...methods}>
        <Text className={styles.label} variant="bodySmall" color={"textSubtle"}>
          Select a task type and client to continue to the form.
        </Text>

        <FormDropdownField
          name="taskType"
          label="Type"
          values={taskTypeOptions}
          getOptionKey={(option) => option.value}
          required="Select a type..."
          disablePortal={false}
          isLoading={isLoadingTaskTypes}
          onClear={() => handleClearField("taskType")}
        />
        <Spacer verticalSpacing="24px" />

        <FormDropdownField
          name="client"
          label="Client"
          values={filteredClientList}
          getOptionKey={(option) => option.value}
          required="Select a client..."
          disablePortal={false}
          onClear={() => handleClearField("client")}
        />

        <Spacer verticalSpacing="16px" />
      </FormProvider>

      <Stack
        spacing={2}
        direction="row"
        marginTop="24px"
        justifyContent="flex-end"
      >
        <Button
          text="Cancel"
          buttonType="primary"
          variant="text"
          disabled={false}
          onClick={onClose}
        />

        <Button
          text="Continue"
          buttonType="primary"
          variant="contained"
          disabled={!isValid}
          onClick={onContinue}
        />
      </Stack>
    </ModalWithClearButton>
  );
};

export default CreateTaskModal;
